import React, { useState, useEffect, useRef } from "react";
import editImg from "./edit.png";
import { DeleteIcon, MinusCircleIcon } from "./SVGs";
import Checkbox from "../../../../Checkbox";
import RegenerateDropdown from "./RegenerateDropdown";
import { toast } from "../../../../utils/appToast";
import api, { getFreshAccessToken } from "../../../chat/api";
import MemoizedMarkdown from "../MemoizedMarkdown";// Imported from your standalone file
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";

const turndownService = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
});
turndownService.use(gfm);


const SubsectionExpandedView = ({
  subsection,
  onClose,
  onSave,
  onRegenerate,
  isSelected,
  onCheckboxChange,
  onEdit,
  onDelete,
  sectionId,
  folderId,
  onRefreshAnalyze,
  wsRef, // Left here for backward compatibility with props, but not used internally now
  connectWebSocket, // Left here for backward compatibility
}) => {
  const [regenerateState, setRegenerateState] = useState("idle");
  const [editedContent, setEditedContent] = useState(subsection.response || "");
  const [originalContent, setOriginalContent] = useState(subsection.response || "");
  const [hasContentChanged, setHasContentChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const contentRef = useRef(null);

  // Force remount of contentEditable div to prevent React removeChild crash
  const [prevResponse, setPrevResponse] = useState(subsection.response);
  const [renderKey, setRenderKey] = useState(0);

  if (subsection.response !== prevResponse) {
    setPrevResponse(subsection.response);
    setRenderKey(renderKey + 1);
    setEditedContent(subsection.response || "");
    setOriginalContent(subsection.response || "");
    setHasContentChanged(false);
  }

  // Handle the save button text display timeout
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
        // Close the edit mode naturally after showing the success state
        if (onSave) onSave();
      }, 1000); 
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, onSave]);

  const handleContentChange = (e) => {
    // Convert edited HTML back to markdown so tables/lists/headings survive the round-trip.
    const newContent = turndownService.turndown(e.currentTarget.innerHTML);
    setEditedContent(newContent);
    setHasContentChanged(newContent !== originalContent);
  };

  const handleSaveContent = async () => {
    if (!hasContentChanged) return;

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const token = await getFreshAccessToken();

      const response = await api.patch(
        `api/v1/rfp/folders/${folderId}/analysis/edit/`,
        {
          subsection_id: subsection.id,
          updated_response: editedContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setOriginalContent(editedContent);
        setHasContentChanged(false);
        
        // Wait for the parent to finish fetching the NEW data before we trigger success
        if (onRefreshAnalyze) {
          await onRefreshAnalyze();
        }

        setIsSaving(false);
        setSaveSuccess(true); // Triggers the timeout above to call onSave()
      } else {
        throw new Error(`Failed to save: ${response.status}`);
      }
    } catch (error) {
      console.error("Save error:", error);
      setIsSaving(false);

      let errorMessage = "Failed to save changes";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleRegenerateSelect = async (option) => {
    if (!subsection.id) {
      toast.error("Invalid subsection");
      return;
    }

    setRegenerateState("loading");

    try {
      const token = await getFreshAccessToken();

      const regenerateData = {
        subsection_id: subsection.id,
        custom_prompt: option.customInstructions || option.label,
      };

      const response = await api.post(
        `api/v1/rfp/folders/${folderId}/regenerate-subsection/`,
        regenerateData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Removed websocket logic; just map the updated data directly via standard refresh.
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 202
      ) {
        setRegenerateState("success");

        if (onRefreshAnalyze) {
          await onRefreshAnalyze();
        }

        setTimeout(() => {
          setRegenerateState("idle");
        }, 2000);
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error("Subsection regeneration error:", error);
      setRegenerateState("idle");

      let errorMessage = "Failed to regenerate subsection";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const getSaveButtonText = () => {
    if (isSaving) return "Saving...";
    if (saveSuccess) return "Saved";
    return "Save";
  };

  return (
    <div className="bg-white border border-[#BB42AE] rounded-lg mt-1 mb-4 shadow-sm overflow-hidden flex flex-col">
      {/* Editable Response Content */}
     <div className="p-4 h-fit min-h-[150px] max-h-[calc(100vh_-_500px)] overflow-y-auto custom-scrollbar">
        {subsection.response ? (
          <div
            key={`editable-${renderKey}`}
            ref={contentRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            onInput={handleContentChange}
            className="prose prose-sm max-w-none text-[#18101C] font-outfit leading-relaxed outline-none focus:outline-none text-[14px]"
          >
            <MemoizedMarkdown content={subsection.response} />
          </div>
        ) : (
          <div
            key={`empty-${renderKey}`}
            ref={contentRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            onInput={handleContentChange}
            className="text-gray-400 italic font-outfit text-[14px] outline-none focus:outline-none min-h-[100px] flex items-center justify-center"
          >
            No content generated yet.
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-3 bg-white">
        <button
          onClick={onClose}
          className="px-6 py-1.5 rounded-md bg-white border border-[#BB42AE] text-[#BB42AE] text-[14px] font-outfit font-medium hover:bg-[#FFECFD] transition-colors"
        >
          Cancel
        </button>

        <button
          onClick={handleSaveContent}
          disabled={!hasContentChanged || isSaving}
          className={`px-6 py-1.5 rounded-md text-[14px] font-outfit font-medium transition-colors ${
            saveSuccess
              ? "bg-[#BB42AE] text-white"
              : !hasContentChanged || isSaving
              ? "bg-[#BB42AE] text-white opacity-60 cursor-not-allowed"
              : "bg-[#BB42AE] text-white hover:opacity-90"
          }`}
        >
          {getSaveButtonText()}
        </button>
      </div>
    </div>
  );
};

export default SubsectionExpandedView;
